import { escapeCell } from '../shared/csv';
import type { AccessGrant } from '../types';

// Re-exported so existing importers (inventory-page) keep their single import
// site; the implementation now lives in the shared CSV util.
export { downloadCsv } from '../shared/csv';

const FILENAME_FILTER_KEYS = [
  'trustStatus',
  'grantScope',
  'mechanism',
  'principalType',
  'assetType',
  'grantOrigin',
  'assetName',
  'principal',
  'resourceAccountId',
  'principalAccountId',
];

function toFileSlug(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

export function buildExportFilename(search: string): string {
  const params = new URLSearchParams(search);
  const segments: string[] = [];

  const searchText = params.get('search');
  if (searchText) {
    const slug = toFileSlug(searchText).slice(0, 20);
    if (slug) {
      segments.push(slug);
    }
  }

  for (const key of FILENAME_FILTER_KEYS) {
    if (segments.length >= 3) {
      break;
    }
    const values = params.getAll(key);
    if (values.length > 0) {
      const slug = toFileSlug(values.join('-')).slice(0, 30);
      if (slug) {
        segments.push(slug);
      }
    }
  }

  const date = new Date().toISOString().slice(0, 10);
  const filterPart = segments.length > 0 ? `-${segments.join('-')}` : '';
  return `resource-access-grants${filterPart}-${date}.csv`;
}

const COLUMNS: {
  header: string;
  value: (g: AccessGrant) => string | number | null | undefined;
}[] = [
  { header: 'id', value: (g) => g.id },
  { header: 'resourceLabel', value: (g) => g.resourceLabel },
  { header: 'assetId', value: (g) => g.assetId },
  { header: 'assetType', value: (g) => g.assetType },
  { header: 'resourceAccountId', value: (g) => g.resourceAccountId },
  { header: 'resourceRegion', value: (g) => g.resourceRegion },
  { header: 'service', value: (g) => g.service },
  { header: 'mechanism', value: (g) => g.mechanism },
  { header: 'grantScope', value: (g) => g.grantScope },
  { header: 'grantOrigin', value: (g) => g.grantOrigin },
  { header: 'principal', value: (g) => g.principal },
  { header: 'principalLabel', value: (g) => g.principalLabel },
  { header: 'principalType', value: (g) => g.principalType },
  { header: 'principalAccountId', value: (g) => g.principalAccountId },
  { header: 'trustStatus', value: (g) => g.trustStatus },
  // Both trust columns are exported raw. An auditor reading the sheet needs to
  // separate a permanently trusted principal from a reviewer's time-boxed
  // decision, and to see which rows lapsed because the permissions changed.
  { header: 'trustedUntil', value: (g) => g.trustedUntil },
  { header: 'trustLapseReason', value: (g) => g.trustLapseReason },
  { header: 'grantee', value: (g) => g.grantee },
  { header: 'reviewDecision', value: (g) => g.reviewDecision },
  { header: 'reviewComment', value: (g) => g.reviewComment },
  { header: 'nextReviewAt', value: (g) => g.nextReviewAt },
  { header: 'reviewedBy', value: (g) => g.reviewedBy },
  { header: 'reviewedAt', value: (g) => g.reviewedAt },
  { header: 'blockedByRcp', value: (g) => String(g.blockedByRcp) },
  {
    header: 'hasRuntimeConditions',
    value: (g) => String(g.hasRuntimeConditions),
  },
  { header: 'firstObservedAt', value: (g) => g.firstObservedAt },
  { header: 'lastObservedAt', value: (g) => g.lastObservedAt },
];

export function grantsToCsv(grants: AccessGrant[]): string {
  const header = COLUMNS.map((c) => c.header).join(',');
  const rows = grants.map((g) =>
    COLUMNS.map((c) => escapeCell(c.value(g))).join(','),
  );
  return [header, ...rows].join('\n');
}
